import {
  BrandPurple,
  color,
  css,
  GlitterGray,
  Heading,
  Lighter,
  Lighter3,
  Modal,
  OnyxGray,
  styled,
  White,
} from 'canoe';
import React, { useState } from 'react';
import { Input, Table, TableHeader, TableHeaderCol, TableRow, TableRowCol } from 'styleguide';

import { useNavigation } from 'commons/hooks';
import TimeAgo from 'dashboard/components/TimeAgo';
import { CREATE_ROUTE } from 'dashboard/pages/Content/pages/Create/constants';
import { SEGMENTS_PATH } from 'dashboard/pages/Segments/constants';
import SearchIcon from 'styleguide/icons/SearchIcon';

const TableFilter = styled.div`
  ${({ theme }) => css`
    align-items: center;
    display: flex;
    color: ${theme.colors.OnyxGrayL10};
    justify-content: space-between;
    margin-bottom: ${theme.spaces.normal};
  `}
`;

const StyledInput = styled(Input)`
  width: 225px;

  input {
    margin-right: 85px;
    padding-left: 37px;
  }
`;

const SearchIconWrapper = styled(SearchIcon)`
  margin-left: 9px;
  position: absolute;
  transform: translateY(-50%);
  top: 50%;
  z-index: 10;
`;

interface TableRowProps {
  active: boolean;
}

const StyledTableRow = styled(TableRow)`
  &&& {
    box-shadow: none;
    border-radius: 4px;
    border: 1px solid
      ${({ active }: TableRowProps) => (active ? color(BrandPurple) : color(GlitterGray))};
    background-color: ${({ active }: TableRowProps) =>
      active ? color(GlitterGray, Lighter3) : color(White)};
  }
`;

interface SelectSegmentsProps {
  segmentList: any[];
  search: string;
  onSetSearch: (value: string) => void;
  selectedSegment: string | null;
  onSetSelectedSegment: (segment: string) => void;
}

const SelectSegments = ({
  segmentList,
  search,
  onSetSearch,
  selectedSegment,
  onSetSelectedSegment,
}: SelectSegmentsProps) => {
  return (
    <>
      <TableFilter>
        <Heading size="XXS" element="h4">
          Select a Segment to link the content to:
        </Heading>
        <StyledInput
          name="segmentFilter"
          placeholder="Search Segment"
          onChange={({ target: { value } }: React.ChangeEvent<HTMLInputElement>) =>
            onSetSearch(value)
          }
          icon={<SearchIconWrapper width="22" />}
        />
      </TableFilter>
      <Table>
        <TableHeader>
          <TableHeaderCol xs={8}>Segment</TableHeaderCol>
          <TableHeaderCol xs={4}>Last Update</TableHeaderCol>
        </TableHeader>

        {segmentList
          .filter(({ name }: { name: string }) =>
            name.toLocaleLowerCase().includes(search.toLocaleLowerCase()),
          )
          .map((segment: { name: string; id: string; lastModifiedAt: string }) => (
            <StyledTableRow
              key={segment.id}
              active={segment.id === selectedSegment}
              onClick={() => onSetSelectedSegment(segment.id)}
            >
              <TableRowCol xs={8}>{segment.name}</TableRowCol>
              <TableRowCol xs={4}>
                {segment.lastModifiedAt && <TimeAgo date={segment.lastModifiedAt} />}
              </TableRowCol>
            </StyledTableRow>
          ))}
      </Table>
    </>
  );
};

const NoUnlinkedSegmentCopy = styled.p`
  font-size: 24px;
  color: ${color(OnyxGray, Lighter)};
  margin-bottom: 42px;
`;

interface Props {
  toggle: () => void;
  onAction: (segmentId: string) => void;
  segmentList: any[];
  actionText: string;
}

export const SegmentActionModal = ({ toggle, segmentList, onAction, actionText }: Props) => {
  const [search, setSearch] = useState<string>('');
  const [selectedSegment, selectSegment] = useState<string | null>(null);
  const navigate = useNavigation();

  const handleClose = () => {
    setSearch('');
    toggle();
  };

  const footerAction = {
    children: segmentList.length ? actionText : 'Create New Segment',
    onClick: () =>
      !segmentList.length
        ? navigate(`${SEGMENTS_PATH}/${CREATE_ROUTE.path}`)
        : selectedSegment && onAction(selectedSegment),
  };

  return (
    <Modal closeOnClickOutside onClose={handleClose} severity="Info">
      <Modal.Header title={actionText} />
      <Modal.Body>
        {segmentList.length ? (
          <SelectSegments
            onSetSearch={setSearch}
            search={search}
            onSetSelectedSegment={selectSegment}
            selectedSegment={selectedSegment}
            segmentList={segmentList}
          />
        ) : (
          <NoUnlinkedSegmentCopy>
            No Segment found yet on this list. Do you want to create a new Segment?
          </NoUnlinkedSegmentCopy>
        )}
      </Modal.Body>

      <Modal.Footer primaryAction={footerAction} />
    </Modal>
  );
};

export default SegmentActionModal;
